import { CATALOGUE, CHAIN_HOP_RADIUS, FIXED_TICK_HZ, TOWERS, isBuildingId } from './data';
import type { BuildingId, CatalogueId, Domain, ProjectileKind, TowerDefinition, TowerId, TunedValue } from './types';

export type UpgradeSlotId = 'a' | 'b' | 'c';
export type TowerUpgradeId =
  | 'missile.heatseeker'
  | 'missile.he'
  | 'missile.ap'
  | 'howitzer.cruise'
  | 'howitzer.railgun'
  | 'howitzer.mortar'
  | 'tesla.stun'
  | 'tesla.voltage'
  | 'tesla.disruptor'
  | 'gatling.disruptor'
  | 'gatling.stun'
  | 'gatling.blaster'
  | 'nulificator.inverter'
  | 'nulificator.v2'
  | 'aa.flak'
  | 'aa.point-defense'
  | 'aa.disruptor'
  | 'goo.acid'
  | 'goo.foam'
  | 'goo.blaster'
  | 'chain.stun'
  | 'chain.mirror'
  | 'chain.battery'
  | 'gauss.splitter'
  | 'gauss.mac'
  | 'gauss.hammer'
  | 'oil.napalm'
  | 'oil.grease';

export type BuildingUpgradeId =
  | 'silo.thermonuclear'
  | 'silo.armament'
  | 'wall.mine-factory'
  | 'wall.wire-factory'
  | 'recycler.chemical-depot'
  | 'recycler.power-plant'
  | 'radar.tactical-atc'
  | 'radar.orbital-uplink'
  | 'laboratory.particle-accelerator'
  | 'laboratory.black-site';

export type StructureUpgradeId = TowerUpgradeId | BuildingUpgradeId;

export interface UpgradeOptionScaffold {
  id: UpgradeSlotId;
  upgradeId: StructureUpgradeId | null;
  name: string | null;
  description: string | null;
  cost: number | null;
  moduleAsset: string | null;
  configured: boolean;
}

export interface StructureUpgradeScaffold {
  structureId: CatalogueId;
  options: readonly UpgradeOptionScaffold[];
}

export interface TowerUpgradeSpec {
  id: TowerUpgradeId;
  parentId: TowerId;
  slot: UpgradeSlotId;
  name: string;
  description: string;
  cost: number;
  moduleAsset: string;
  damage?: number;
  range?: number;
  domains?: readonly Domain[];
  splashRadius?: number;
  projectile?: ProjectileKind;
  projectileSpeed?: number;
  projectileTurnRate?: number;
  firingSpeedMultiplier?: number;
  reloadTicks?: number;
  piercesShield?: boolean;
  clearsEffects?: boolean;
  retargetsOnDestroyed?: boolean;
  shieldDamageMultiplier?: number;
  unshieldedDamageMultiplier?: number;
  stun?: { chance: number; ticks: number };
  splitDamage?: boolean;
  invertsEffects?: { magnitude: number };
  slowPercent?: number;
  corrosion?: { damagePerSecond: number; ticks: number };
  chainTargets?: number;
  chainBounceHits?: number;
  splitShot?: { targets: number; damage: number; radius: number };
  targetLockTicks?: number;
  orbitalStrike?: { groundDamage: number; groundSplashRadius: number };
  confusePercent?: number;
  burning?: { damagePerSecond: number; ticks: number };
}

export interface BuildingUpgradeSpec {
  id: BuildingUpgradeId;
  parentId: BuildingId;
  slot: 'a' | 'b';
  name: string;
  description: string;
  cost: number;
  moduleAsset: string;
  requirement: string;
  unlocks: readonly TowerUpgradeId[];
}

export interface ResolvedTowerDefinition extends TowerDefinition {
  upgradeId?: TowerUpgradeId;
  moduleAsset?: string;
  retargetsOnDestroyed?: boolean;
  shieldDamageMultiplier?: number;
  unshieldedDamageMultiplier?: number;
  stun?: { chance: number; ticks: number };
  splitDamage?: boolean;
  invertsEffects?: { magnitude: number };
  corrosion?: { damagePerSecond: number; ticks: number };
  chainBounceHits?: number;
  splitShot?: { targets: number; damage: number; radius: number };
  targetLockTicks?: number;
  orbitalStrike?: { groundDamage: number; groundSplashRadius: number };
  burning?: { damagePerSecond: number; ticks: number };
}

const authoredValue = (value: number, field: string, upgrade: string): TunedValue => ({
  value,
  provenance: {
    status: 'AUTHORED',
    sourceIds: ['USER-CANYON-DEFENSE-PLUS-UPGRADES'],
    note: `${upgrade}: user-authored ${field}.`,
  },
});

const secondsToTicks = (seconds: number): number => Math.round(seconds * FIXED_TICK_HZ);

export const TOWER_UPGRADES: Readonly<Record<TowerUpgradeId, TowerUpgradeSpec>> = {
  'missile.heatseeker': {
    id: 'missile.heatseeker',
    parentId: 'cd1.tower.missile',
    slot: 'a',
    name: 'Heatseeker Turret',
    description: 'All targets · 10 damage · 110 range · reacquires if its target is destroyed',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-missile-heatseeker-module.png',
    damage: 10,
    range: 110,
    domains: ['ground', 'air'],
    retargetsOnDestroyed: true,
  },
  'missile.he': {
    id: 'missile.he',
    parentId: 'cd1.tower.missile',
    slot: 'b',
    name: 'HE Missile Turret',
    description: 'Ground · 15 damage · 15 splash · 110 range · homing',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-missile-he-module.png',
    damage: 15,
    range: 110,
    splashRadius: 15,
  },
  'missile.ap': {
    id: 'missile.ap',
    parentId: 'cd1.tower.missile',
    slot: 'c',
    name: 'AP Missile Turret',
    description: 'Ground · 30 damage · 100 range · homing',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-missile-ap-module.png',
    damage: 30,
    range: 100,
  },
  'howitzer.cruise': {
    id: 'howitzer.cruise',
    parentId: 'cd1.tower.howitzer',
    slot: 'a',
    name: 'Cruise Missile',
    description: 'Ground · 50 damage · 25 splash · 250 range · homing · 0.7× fire rate',
    cost: 650,
    moduleAsset: 'assets/units/upgrade-howitzer-cruise-module.png',
    damage: 50,
    range: 250,
    splashRadius: 25,
    projectile: 'cruiseMissile',
    firingSpeedMultiplier: 0.7,
  },
  'howitzer.railgun': {
    id: 'howitzer.railgun',
    parentId: 'cd1.tower.howitzer',
    slot: 'b',
    name: 'Railgun',
    description: 'Ground · 100 damage · 25 inherited splash · 160 range · instant · 0.5× fire rate',
    cost: 700,
    moduleAsset: 'assets/units/upgrade-howitzer-railgun-module.png',
    damage: 100,
    range: 160,
    projectile: 'gaussRail',
    projectileSpeed: 0,
    projectileTurnRate: 0,
    firingSpeedMultiplier: 0.5,
  },
  'howitzer.mortar': {
    id: 'howitzer.mortar',
    parentId: 'cd1.tower.howitzer',
    slot: 'c',
    name: 'Mortar',
    description: 'Ground · 15 damage · 25 splash · 200 range · 3× fire rate',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-howitzer-mortar-module.png',
    damage: 15,
    range: 200,
    splashRadius: 25,
    projectile: 'mortarShell',
    firingSpeedMultiplier: 3,
  },
  'tesla.stun': {
    id: 'tesla.stun',
    parentId: 'cd1.tower.tesla',
    slot: 'a',
    name: 'Stun Tower',
    description: 'Ground pulse · 10 damage · 60 radius · 50% chance to stun for 1.5s',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-tesla-stun-module.png',
    damage: 10,
    range: 60,
    splashRadius: 60,
    stun: { chance: 0.5, ticks: secondsToTicks(1.5) },
  },
  'tesla.voltage': {
    id: 'tesla.voltage',
    parentId: 'cd1.tower.tesla',
    slot: 'b',
    name: 'Voltage Tower',
    description: 'Ground / Air pulse · 80 total damage divided equally among targets · 60 radius',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-tesla-voltage-module.png',
    damage: 80,
    range: 60,
    domains: ['ground', 'air'],
    splashRadius: 60,
    splitDamage: true,
  },
  'tesla.disruptor': {
    id: 'tesla.disruptor',
    parentId: 'cd1.tower.tesla',
    slot: 'c',
    name: 'Tesla Disruptor',
    description: 'Ground pulse · 20 damage · 60 radius · pierces shields · 4× shielded / 0.5× unshielded',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-tesla-disruptor-module.png',
    damage: 20,
    range: 60,
    splashRadius: 60,
    piercesShield: true,
    shieldDamageMultiplier: 4,
    unshieldedDamageMultiplier: 0.5,
  },
  'gatling.disruptor': {
    id: 'gatling.disruptor',
    parentId: 'cd1.tower.gatling',
    slot: 'a',
    name: 'Gatling Disruptor',
    description: 'Ground · 3 damage · pierces shields · 70 range · 4× shielded / 0.5× unshielded',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-gatling-disruptor-module.png',
    damage: 3,
    range: 70,
    shieldDamageMultiplier: 4,
    unshieldedDamageMultiplier: 0.5,
  },
  'gatling.stun': {
    id: 'gatling.stun',
    parentId: 'cd1.tower.gatling',
    slot: 'b',
    name: 'Stun Laser',
    description: 'Ground · 3 damage · pierces shields · 70 range · 10% chance to stun for 1.5s',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-gatling-stun-module.png',
    damage: 3,
    range: 70,
    stun: { chance: 0.1, ticks: secondsToTicks(1.5) },
  },
  'gatling.blaster': {
    id: 'gatling.blaster',
    parentId: 'cd1.tower.gatling',
    slot: 'c',
    name: 'Gatling Blaster',
    description: 'Ground · 3 damage · pierces shields · 70 range · 10 splash',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-gatling-blaster-module.png',
    damage: 3,
    range: 70,
    splashRadius: 10,
  },
  'nulificator.inverter': {
    id: 'nulificator.inverter',
    parentId: 'cd1.tower.nulificator',
    slot: 'a',
    name: 'Inverter',
    description: 'All targets · inverts effects at 0.5× magnitude · 100 range · 0.5× fire rate',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-nulificator-inverter-module.png',
    range: 100,
    firingSpeedMultiplier: 0.5,
    clearsEffects: false,
    invertsEffects: { magnitude: 0.5 },
  },
  'nulificator.v2': {
    id: 'nulificator.v2',
    parentId: 'cd1.tower.nulificator',
    slot: 'b',
    name: 'Nulificator V2',
    description: 'All targets · clears effects · 100 range · 15 splash',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-nulificator-v2-module.png',
    range: 100,
    splashRadius: 15,
  },
  'aa.flak': {
    id: 'aa.flak',
    parentId: 'cd1.tower.aa',
    slot: 'a',
    name: 'Flak Gun',
    description: 'Air · 2 damage · rapid · 120 range · 20 splash',
    cost: 500,
    moduleAsset: 'assets/units/upgrade-aa-flak-module.png',
    damage: 2,
    range: 120,
    splashRadius: 20,
  },
  'aa.point-defense': {
    id: 'aa.point-defense',
    parentId: 'cd1.tower.aa',
    slot: 'b',
    name: 'Point Defense',
    description: 'Air · 5 damage · rapid · 80 range',
    cost: 500,
    moduleAsset: 'assets/units/upgrade-aa-point-defense-module.png',
    damage: 5,
    range: 80,
  },
  'aa.disruptor': {
    id: 'aa.disruptor',
    parentId: 'cd1.tower.aa',
    slot: 'c',
    name: 'AA Disruptor',
    description: 'Air · 3 damage · rapid · 110 range · pierces shields · 4× shielded / 0.5× unshielded',
    cost: 500,
    moduleAsset: 'assets/units/upgrade-aa-disruptor-module.png',
    damage: 3,
    range: 110,
    piercesShield: true,
    shieldDamageMultiplier: 4,
    unshieldedDamageMultiplier: 0.5,
  },
  'goo.acid': {
    id: 'goo.acid',
    parentId: 'cd1.tower.goo',
    slot: 'a',
    name: 'Acid Cannon',
    description: 'All targets · slows 15% · 15 splash · 90 range · stacks 1 DPS Corrosion for 8s against unshielded',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-goo-acid-module.png',
    splashRadius: 15,
    range: 90,
    slowPercent: 0.15,
    corrosion: { damagePerSecond: 1, ticks: secondsToTicks(8) },
  },
  'goo.foam': {
    id: 'goo.foam',
    parentId: 'cd1.tower.goo',
    slot: 'b',
    name: 'Foam Cannon',
    description: 'Ground only · slows 65% · 15 splash · 90 range',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-goo-foam-module.png',
    domains: ['ground'],
    splashRadius: 15,
    range: 90,
    slowPercent: 0.65,
  },
  'goo.blaster': {
    id: 'goo.blaster',
    parentId: 'cd1.tower.goo',
    slot: 'c',
    name: 'Goo Blaster',
    description: 'All targets · slows 35% · 30 splash · 120 range',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-goo-blaster-module.png',
    splashRadius: 30,
    range: 120,
    slowPercent: 0.35,
  },
  'chain.stun': {
    id: 'chain.stun',
    parentId: 'cd1.tower.chain',
    slot: 'a',
    name: 'Chain Stun',
    description: 'Ground · 8 damage · chains 3 · 100 range · 25% chance to stun for 1.5s',
    cost: 500,
    moduleAsset: 'assets/units/upgrade-chain-stun-module.png',
    damage: 8,
    range: 100,
    stun: { chance: 0.25, ticks: secondsToTicks(1.5) },
  },
  'chain.mirror': {
    id: 'chain.mirror',
    parentId: 'cd1.tower.chain',
    slot: 'b',
    name: 'Chain Mirror',
    description: 'Ground · 8 damage · two targets · alternates for up to 8 total hits · 100 range',
    cost: 500,
    moduleAsset: 'assets/units/upgrade-chain-mirror-module.png',
    damage: 8,
    range: 100,
    chainTargets: 2,
    chainBounceHits: 8,
  },
  'chain.battery': {
    id: 'chain.battery',
    parentId: 'cd1.tower.chain',
    slot: 'c',
    name: 'Chain Battery',
    description: 'All targets · 8 damage · chains 3 · 120 range',
    cost: 500,
    moduleAsset: 'assets/units/upgrade-chain-battery-module.png',
    damage: 8,
    range: 120,
    domains: ['ground', 'air'],
  },
  'gauss.splitter': {
    id: 'gauss.splitter',
    parentId: 'cd1.tower.gauss',
    slot: 'a',
    name: 'Splitter Cannon',
    description: 'Air · 12 piercing damage · 90 range · two nearby air targets take 8 piercing damage',
    cost: 600,
    moduleAsset: 'assets/units/upgrade-gauss-splitter-module.png',
    damage: 12,
    range: 90,
    piercesShield: true,
    splitShot: { targets: 2, damage: 8, radius: CHAIN_HOP_RADIUS * 2 },
  },
  'gauss.mac': {
    id: 'gauss.mac',
    parentId: 'cd1.tower.gauss',
    slot: 'b',
    name: 'MAC Installation',
    description: 'Air · 45 piercing damage · 130 range · 0.5× fire rate',
    cost: 1000,
    moduleAsset: 'assets/units/upgrade-gauss-mac-module.png',
    damage: 45,
    range: 130,
    piercesShield: true,
    firingSpeedMultiplier: 0.5,
  },
  'gauss.hammer': {
    id: 'gauss.hammer',
    parentId: 'cd1.tower.gauss',
    slot: 'c',
    name: 'Hammer of God',
    description: 'Air · 200 piercing damage · 5 air splash · 110 range · 1.5s lock · 3.5s cooldown · 100 ground damage / 30 splash',
    cost: 1000,
    moduleAsset: 'assets/units/upgrade-gauss-hammer-module.png',
    damage: 200,
    range: 110,
    splashRadius: 5,
    piercesShield: true,
    reloadTicks: secondsToTicks(3.5),
    targetLockTicks: secondsToTicks(1.5),
    orbitalStrike: { groundDamage: 100, groundSplashRadius: 30 },
  },
  'oil.napalm': {
    id: 'oil.napalm',
    parentId: 'cd1.tower.oil',
    slot: 'a',
    name: 'Napalm Spray',
    description: 'Ground · 10 damage · turning -50% · 120 range · 15 splash · stacks 10 DPS Burning for 3s against unshielded',
    cost: 1000,
    moduleAsset: 'assets/units/upgrade-oil-napalm-module.png',
    damage: 10,
    range: 120,
    splashRadius: 15,
    confusePercent: 0.5,
    burning: { damagePerSecond: 10, ticks: secondsToTicks(3) },
  },
  'oil.grease': {
    id: 'oil.grease',
    parentId: 'cd1.tower.oil',
    slot: 'b',
    name: 'Grease Spray',
    description: 'Ground · 3 damage · turning -90% · 140 range · 25 splash',
    cost: 1000,
    moduleAsset: 'assets/units/upgrade-oil-grease-module.png',
    damage: 3,
    range: 140,
    splashRadius: 25,
    confusePercent: 0.9,
  },
};

export const BUILDING_UPGRADES: Readonly<Record<BuildingUpgradeId, BuildingUpgradeSpec>> = {
  'silo.thermonuclear': {
    id: 'silo.thermonuclear',
    parentId: 'cd1.building.silo',
    slot: 'a',
    name: 'Thermonuclear Silo',
    description: 'Missile source · missile damage 90, then +30 per additional Thermonuclear Silo',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-building-thermonuclear-silo.png',
    requirement: 'Have 3 distinct enemies survive Missile hits',
    unlocks: [],
  },
  'silo.armament': {
    id: 'silo.armament',
    parentId: 'cd1.building.silo',
    slot: 'b',
    name: 'Armament Depot',
    description: 'Missile source · unlocks Flak, HE Missile, AP Missile, and Mortar',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-building-armament-depot.png',
    requirement: 'Build 2 Howitzers',
    unlocks: ['aa.flak', 'missile.he', 'missile.ap', 'howitzer.mortar'],
  },
  'wall.mine-factory': {
    id: 'wall.mine-factory',
    parentId: 'cd1.building.wall-factory',
    slot: 'a',
    name: 'Mine Factory',
    description: 'Mine source · no longer provides Wall',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-building-mine-factory.png',
    requirement: 'Kill 50 enemies with area attacks',
    unlocks: [],
  },
  'wall.wire-factory': {
    id: 'wall.wire-factory',
    parentId: 'cd1.building.wall-factory',
    slot: 'b',
    name: 'Wire Factory',
    description: 'Barbed Wire source · no longer provides Wall',
    cost: 800,
    moduleAsset: 'assets/units/upgrade-building-wire-factory.png',
    requirement: 'Build 15 Walls',
    unlocks: [],
  },
  'recycler.chemical-depot': {
    id: 'recycler.chemical-depot',
    parentId: 'cd1.building.recycler',
    slot: 'a',
    name: 'Chemical Depot',
    description: 'Recycler · unlocks Acid, Foam, Goo Blaster, and Grease Spray',
    cost: 900,
    moduleAsset: 'assets/units/upgrade-building-chemical-depot.png',
    requirement: 'Slow 100 distinct enemies',
    unlocks: ['goo.acid', 'goo.foam', 'goo.blaster', 'oil.grease'],
  },
  'recycler.power-plant': {
    id: 'recycler.power-plant',
    parentId: 'cd1.building.recycler',
    slot: 'b',
    name: 'Power Plant',
    description: 'Recycler · unlocks Stun Tower, Voltage Tower, Stun Laser, and Chain Stun',
    cost: 900,
    moduleAsset: 'assets/units/upgrade-building-power-plant.png',
    requirement: 'Fire Tesla Towers 100 times',
    unlocks: ['tesla.stun', 'tesla.voltage', 'gatling.stun', 'chain.stun'],
  },
  'radar.tactical-atc': {
    id: 'radar.tactical-atc',
    parentId: 'cd1.building.radar',
    slot: 'a',
    name: 'Tactical ATC',
    description: 'Radar +10% range · unlocks Heatseeker, Cruise Missile, Point Defense, and Chain Battery',
    cost: 1000,
    moduleAsset: 'assets/units/upgrade-building-tactical-atc.png',
    requirement: 'Build 5 Gauss Cannons',
    unlocks: ['missile.heatseeker', 'howitzer.cruise', 'aa.point-defense', 'chain.battery'],
  },
  'radar.orbital-uplink': {
    id: 'radar.orbital-uplink',
    parentId: 'cd1.building.radar',
    slot: 'b',
    name: 'Orbital Uplink',
    description: 'Tower range +15% · unlocks Gatling Blaster and Hammer of God',
    cost: 1000,
    moduleAsset: 'assets/units/upgrade-building-orbital-uplink.png',
    requirement: 'Build a Thermonuclear Silo',
    unlocks: ['gatling.blaster', 'gauss.hammer'],
  },
  'laboratory.particle-accelerator': {
    id: 'laboratory.particle-accelerator',
    parentId: 'cd1.building.laboratory',
    slot: 'a',
    name: 'Particle Accelerator',
    description: 'Stop Time source · unlocks six disruptive weapon paths',
    cost: 1200,
    moduleAsset: 'assets/units/upgrade-building-particle-accelerator.png',
    requirement: 'Fire Hammer of God once',
    unlocks: ['aa.disruptor', 'chain.mirror', 'gauss.mac', 'howitzer.railgun', 'tesla.disruptor', 'gatling.disruptor'],
  },
  'laboratory.black-site': {
    id: 'laboratory.black-site',
    parentId: 'cd1.building.laboratory',
    slot: 'b',
    name: 'Black Site',
    description: 'Stop Time source · unlocks Splitter, Napalm, Inverter, and Nulificator V2',
    cost: 1200,
    moduleAsset: 'assets/units/upgrade-building-black-site.png',
    requirement: 'Build 2 Oil Sprays and 2 Nulificators',
    unlocks: ['gauss.splitter', 'oil.napalm', 'nulificator.inverter', 'nulificator.v2'],
  },
};

const placeholderOptions = (count: 2 | 3): readonly UpgradeOptionScaffold[] =>
  (['a', 'b', 'c'] as const).slice(0, count).map((id) => ({
    id,
    upgradeId: null,
    name: null,
    description: null,
    cost: null,
    moduleAsset: null,
    configured: false,
  }));

const towerOptions = (towerId: TowerId): readonly UpgradeOptionScaffold[] => Object.values(TOWER_UPGRADES)
  .filter((upgrade) => upgrade.parentId === towerId)
  .sort((left, right) => left.slot.localeCompare(right.slot))
  .map((upgrade) => ({
    id: upgrade.slot,
    upgradeId: upgrade.id,
    name: upgrade.name,
    description: upgrade.description,
    cost: upgrade.cost,
    moduleAsset: upgrade.moduleAsset,
    configured: true,
  }));

const buildingOptions = (buildingId: BuildingId): readonly UpgradeOptionScaffold[] => Object.values(BUILDING_UPGRADES)
  .filter((upgrade) => upgrade.parentId === buildingId)
  .sort((left, right) => left.slot.localeCompare(right.slot))
  .map((upgrade) => ({
    id: upgrade.slot,
    upgradeId: upgrade.id,
    name: upgrade.name,
    description: upgrade.description,
    cost: upgrade.cost,
    moduleAsset: upgrade.moduleAsset,
    configured: true,
  }));

/**
 * Central extension point for Canyon Defense+ upgrades.
 * Configured towers expose their approved branches; remaining structures retain inert placeholders.
 */
export const STRUCTURE_UPGRADE_SCAFFOLDS = Object.fromEntries(
  CATALOGUE.map((entry) => {
    const configured = isBuildingId(entry.id) ? buildingOptions(entry.id) : towerOptions(entry.id as TowerId);
    return [entry.id, {
      structureId: entry.id,
      options: configured.length > 0 ? configured : placeholderOptions(isBuildingId(entry.id) ? 2 : 3),
    }];
  }),
) as Readonly<Record<CatalogueId, StructureUpgradeScaffold>>;

export const upgradeScaffoldFor = (structureId: CatalogueId): StructureUpgradeScaffold =>
  STRUCTURE_UPGRADE_SCAFFOLDS[structureId];

export const isTowerUpgradeId = (value: string | undefined): value is TowerUpgradeId =>
  value !== undefined && Object.prototype.hasOwnProperty.call(TOWER_UPGRADES, value);

export const isBuildingUpgradeId = (value: string | undefined): value is BuildingUpgradeId =>
  value !== undefined && Object.prototype.hasOwnProperty.call(BUILDING_UPGRADES, value);

export const resolveTowerDefinition = (
  towerId: TowerId,
  upgradeId?: TowerUpgradeId,
): ResolvedTowerDefinition => {
  const parent = TOWERS[towerId];
  if (!upgradeId) return parent;
  const upgrade = TOWER_UPGRADES[upgradeId];
  if (!upgrade || upgrade.parentId !== towerId) return parent;
  const reloadTicks = upgrade.reloadTicks !== undefined
    ? authoredValue(upgrade.reloadTicks, 'reload ticks', upgrade.name)
    : upgrade.firingSpeedMultiplier === undefined
      ? parent.reloadTicks
      : authoredValue(
      Math.max(1, Math.round(parent.reloadTicks.value / upgrade.firingSpeedMultiplier)),
      'reload ticks',
      upgrade.name,
      );
  return {
    ...parent,
    label: upgrade.name,
    cost: authoredValue(parent.cost.value + upgrade.cost, 'total invested cost', upgrade.name),
    domains: upgrade.domains ?? parent.domains,
    range: upgrade.range === undefined ? parent.range : authoredValue(upgrade.range, 'range', upgrade.name),
    damage: upgrade.damage === undefined ? parent.damage : authoredValue(upgrade.damage, 'damage', upgrade.name),
    reloadTicks,
    projectileSpeed: upgrade.projectileSpeed === undefined
      ? parent.projectileSpeed
      : authoredValue(upgrade.projectileSpeed, 'projectile speed', upgrade.name),
    projectileTurnRate: upgrade.projectileTurnRate === undefined
      ? parent.projectileTurnRate
      : authoredValue(upgrade.projectileTurnRate, 'projectile turn rate', upgrade.name),
    projectile: upgrade.projectile ?? parent.projectile,
    splashRadius: upgrade.splashRadius ?? parent.splashRadius,
    piercesShield: upgrade.piercesShield ?? parent.piercesShield,
    clearsEffects: upgrade.clearsEffects ?? parent.clearsEffects,
    chainTargets: upgrade.chainTargets ?? parent.chainTargets,
    chainRadius: parent.chainRadius,
    slow: upgrade.slowPercent === undefined
      ? parent.slow
      : parent.slow ? { ...parent.slow, percent: upgrade.slowPercent } : undefined,
    confuse: upgrade.confusePercent === undefined
      ? parent.confuse
      : parent.confuse ? { ...parent.confuse, percent: upgrade.confusePercent } : undefined,
    upgradeId: upgrade.id,
    moduleAsset: upgrade.moduleAsset,
    retargetsOnDestroyed: upgrade.retargetsOnDestroyed,
    shieldDamageMultiplier: upgrade.shieldDamageMultiplier,
    unshieldedDamageMultiplier: upgrade.unshieldedDamageMultiplier,
    stun: upgrade.stun,
    splitDamage: upgrade.splitDamage,
    invertsEffects: upgrade.invertsEffects,
    corrosion: upgrade.corrosion,
    chainBounceHits: upgrade.chainBounceHits,
    splitShot: upgrade.splitShot,
    targetLockTicks: upgrade.targetLockTicks,
    orbitalStrike: upgrade.orbitalStrike,
    burning: upgrade.burning,
  };
};
